import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import { apiErrorText } from '../../api/errorText';
import type { LiveTvLineupChannel } from '../../api/types';
import { useApi } from '../../api/useApi';

interface ChannelOverrideDialogProps {
  entry: LiveTvLineupChannel;
  onClose: () => void;
  onSaved: () => void;
}

/// What is changed about one channel: its name, its number, the guide it takes its programmes
/// from, and whether it is listed. Every field left blank means "as the plugin has it", which is
/// why the plugin's own value is shown in its place.
export default function ChannelOverrideDialog({ entry, onClose, onSaved }: ChannelOverrideDialogProps) {
  const { t } = useTranslation();
  const original = entry.original;

  const [name, setName] = useState(entry.override?.name ?? '');
  const [number, setNumber] = useState(entry.override?.number ?? '');
  const [guideId, setGuideId] = useState(entry.override?.guideId ?? '');
  const [hidden, setHidden] = useState(entry.override?.hidden ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  // What the guides have programmes for, offered while typing - matching a satellite channel to
  // an XMLTV feed is picking from this list, not guessing a spelling.
  const guideChannels = useApi(['livetv', 'guideChannels'], api.liveTvGuideChannels, { staleTime: 300_000 });

  const save = async (values: { name: string; number: string; guideId: string; hidden: boolean }) => {
    setBusy(true);
    setError(undefined);

    try {
      await api.saveLiveTvOverride({
        providerId: original.providerId,
        channelId: original.id,
        name: values.name.trim() || null,
        number: values.number.trim() || null,
        guideId: values.guideId.trim() || null,
        hidden: values.hidden,
      });
      onSaved();
    } catch (caught) {
      const text = apiErrorText(caught, t);
      setError([text.message, text.remedy].filter(Boolean).join(' '));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('livetvChannels.override.title', { name: original.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('livetvChannels.override.hint')}
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label={t('livetvChannels.override.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={original.name}
            helperText={t('livetvChannels.override.fromPlugin', { value: original.name })}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <TextField
            label={t('livetvChannels.override.number')}
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            placeholder={original.number ?? ''}
            helperText={
              original.number
                ? t('livetvChannels.override.fromPlugin', { value: original.number })
                : t('livetvChannels.override.noNumber')
            }
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { inputMode: 'decimal' } }}
          />

          <Autocomplete
            freeSolo
            options={guideChannels.data ?? []}
            inputValue={guideId}
            onInputChange={(_, value) => setGuideId(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('livetvChannels.override.guideId')}
                placeholder={original.guideId ?? original.id}
                helperText={
                  original.guideId
                    ? t('livetvChannels.override.fromPlugin', { value: original.guideId })
                    : t('livetvChannels.override.guideIdHint')
                }
                slotProps={{ ...params.slotProps, inputLabel: { ...params.slotProps.inputLabel, shrink: true } }}
              />
            )}
          />

          <FormControlLabel
            control={<Switch checked={hidden} onChange={(event) => setHidden(event.target.checked)} />}
            label={
              <Stack sx={{ py: 0.5 }}>
                <Typography>{t('livetvChannels.override.hidden')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('livetvChannels.override.hiddenHint')}
                </Typography>
              </Stack>
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button
          color="inherit"
          disabled={busy || entry.override === null}
          onClick={() => void save({ name: '', number: '', guideId: '', hidden: false })}
          sx={{ marginInlineEnd: 'auto' }}
        >
          {t('livetvChannels.override.reset')}
        </Button>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={busy} onClick={() => void save({ name, number, guideId, hidden })}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
